import { ArrowRight, CalendarPlus, Clock, DollarSign } from 'lucide-react';
import type { LoanExtension } from '@/types';

interface Props {
  extensions: LoanExtension[];
}

function fmtCurrency(v: number | string): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
  }).format(n);
}

function fmtDate(d: string | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function ExtensionHistoryPanel({ extensions }: Props) {
  if (!extensions || extensions.length === 0) return null;

  const totalFees = extensions.reduce((sum, e) => sum + parseFloat(e.extensionFee || '0'), 0);
  const totalMonthsAdded = extensions.reduce((sum, e) => sum + e.additionalMonths, 0);

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarPlus className="w-4 h-4 text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-900">Term Extensions</h3>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            {extensions.length} extension{extensions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />+{totalMonthsAdded} months
          </span>
          {totalFees > 0 && (
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              {fmtCurrency(totalFees)} in fees
            </span>
          )}
        </div>
      </div>

      {/* Extension timeline */}
      <div className="space-y-2">
        {extensions.map(ext => (
          <div key={ext.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                  #{ext.extensionNumber}
                </span>
                <span className="text-xs text-gray-500">{fmtDate(ext.createdAt)}</span>
              </div>
              {parseFloat(ext.extensionFee || '0') > 0 && (
                <span className="text-xs font-semibold text-blue-700">
                  {fmtCurrency(ext.extensionFee)}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {/* Term change */}
              <div className="flex items-center gap-1.5 text-gray-600">
                <span>Term:</span>
                <span className="font-medium text-gray-900">{ext.previousTermMonths}mo</span>
                <ArrowRight className="w-3 h-3 text-blue-500" />
                <span className="font-medium text-blue-700">{ext.newTermMonths}mo</span>
                <span className="text-gray-400">(+{ext.additionalMonths})</span>
              </div>

              {/* Maturity change */}
              <div className="flex items-center gap-1.5 text-gray-600">
                <span>Maturity:</span>
                <span className="font-medium text-gray-900">
                  {fmtDate(ext.previousMaturityDate)}
                </span>
                <ArrowRight className="w-3 h-3 text-blue-500" />
                <span className="font-medium text-blue-700">{fmtDate(ext.newMaturityDate)}</span>
              </div>

              {/* Rate change */}
              {ext.newInterestRate && ext.previousInterestRate && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <span>Rate:</span>
                  <span className="font-medium text-gray-900">
                    {parseFloat(ext.previousInterestRate).toFixed(2)}%
                  </span>
                  <ArrowRight className="w-3 h-3 text-amber-500" />
                  <span className="font-medium text-amber-700">
                    {parseFloat(ext.newInterestRate).toFixed(2)}%
                  </span>
                </div>
              )}

              {/* Fee split */}
              {parseFloat(ext.extensionFee || '0') > 0 && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <span>Lender keeps:</span>
                  <span className="font-medium text-indigo-700">
                    {fmtCurrency(ext.extensionFeeRetained)}
                  </span>
                  {parseFloat(ext.extensionFeePaidOut || '0') > 0 && (
                    <>
                      <span className="text-gray-400">|</span>
                      <span>Paid out:</span>
                      <span className="font-medium text-red-600">
                        {fmtCurrency(ext.extensionFeePaidOut)}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            {ext.reason && <p className="text-xs text-gray-500 mt-1.5 italic">{ext.reason}</p>}

            {ext.approvedBy && (
              <p className="text-[10px] text-gray-400 mt-1">
                Approved by {ext.approvedBy.firstName} {ext.approvedBy.lastName}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
